import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { useDebounce } from "../../hooks/useDebounce";
import { toQuery } from "../../helpers/general";
import apiService from "../../service/apiService";
import { general } from "../../locales/general";
import BoxWrapper from "../../components/BoxWrapper";
import RenderTable from "../../components/RenderTable";
import Pagination from "../../components/Pagination";
import AdminRoute from "../auth/RestrictedRoute";
import RewardList from "./rewardList";
import { Gem, PlusIcon, Settings } from "lucide-react";
import { CSpinner } from "@coreui/react";

interface Rewards {
  meta: PaginationMeta;
  items: Reward[];
}

export default function RewardsPage() {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [isActive, setIsActive] = useState("");
  const [isReusable, setIsReusable] = useState("");
  const [showConfigModal, setShowConfigModal] = useState(false);
  // const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [points, setPoints] = useState<string>("");
  const [configType, setConfigType] = useState("");

  const [selectedRewards, setSelectedRewards] = useState<string[]>([]);

  const searchQuery = useDebounce(query, 300);
  const queryClient = useQueryClient();

  const endpoint = `rewards${toQuery({
    page: currentPage,
    query: searchQuery,
    type,
    isActive,
    isReusable,
  })}`;

  const { data, isLoading } = useQuery<Rewards>({
    queryKey: ["getRewards", endpoint],
    queryFn: () => apiService.MakeGetRequest(endpoint, token),
  });

  const deleteRewardMutation = useMutation({
    mutationFn: (id: string) =>
      apiService.MakeDeleteRequest(`rewards/${id}`, token),
  });

  const deleteReward = (id: string) => {
    deleteRewardMutation.mutate(id, {
      onSuccess: () => {
        toastSuccess("Reward deleted successfully");

        queryClient.invalidateQueries({ queryKey: ["getRewards"] });

        // remove from selection
        setSelectedRewards((prev) =>
          prev.filter((rewardId) => rewardId !== id),
        );
      },
      onError: () => {
        toastError("Operation failed");
      },
    });
  };

  const handleRewardSelection = (id: string, action: "add" | "remove") => {
    if (action === "add") {
      setSelectedRewards((prev) => (prev.includes(id) ? prev : [...prev, id]));
    } else {
      setSelectedRewards((prev) => prev.filter((rewardId) => rewardId !== id));
    }
  };

  const handleBulkDelete = () => {
    selectedRewards.forEach((id) => deleteReward(id));
  };

  const rewards = data?.items ?? [];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: configData } = useQuery({
    queryKey: ["rewardConfig"],
    queryFn: () => apiService.MakeGetRequest(`rewards/configs?'RIDE'`, token),
    enabled: showConfigModal,
    onSuccess: (data) => {
      setPoints(data?.basePoints || "");
      setConfigType(data?.type || "");
    },
  });



  const updatePointsMutation = useMutation({
    mutationFn: ({ points, type }: { points: number; type: string }) => {
      return apiService.MakePutRequest(
        `rewards/configs`,
        {
          basePoints: Number(points),
          type: type,
        },
        token,
      );
    },
    onSuccess: () => {
      toastSuccess("Points updated successfully");
      setShowConfigModal(false);
    },
    onError: () => toastError("Failed to update points"),
  });

  return (
    <AdminRoute>
      <section>
        <h5 className="fs-4 fw-bold mb-2">Bonus </h5>

        <BoxWrapper>
          {/* Header */}
          <div className="d-flex align-items-end justify-content-end gap-2">
            <div
              className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3 "
              style={{ borderTopRightRadius: "8px", alignSelf: "flex-start" }}
            >
              <Gem width="20" color="white" />
              <p className="fs-5 fw-bold m-0 text-white text-pre">
                {data?.meta.totalItems} Bonus
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="d-flex justify-content-between mt-3 px-3">
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary text-white d-flex align-items-center gap-2"
                onClick={() => setShowConfigModal(true)}
              >
                <Settings width="20" height="20" />
                Configurer les points
              </button>
              <Link
                to="/rewards/new"
                className="btn btn-primary text-white d-flex align-items-center gap-2"
              >
                <PlusIcon width="20" height="20" fill="#ffffff" />
                Ajouter un Bonus
              </Link>

              {selectedRewards.length > 0 && (
                <button
                  className="btn btn-danger"
                  onClick={handleBulkDelete}
                  disabled={deleteRewardMutation.isLoading}
                >
                  Supprimer ({selectedRewards.length})
                </button>
              )}
            </div>

            <div className="d-flex gap-2 ">
              {/* Type */}
              <select
                className="form-select"
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="">Tous les types</option>
                <option value="OFFER">Offre</option>
                <option value="PACK">Pack</option>
                <option value="EVENT">Événement</option>
                <option value="DISCOUNT">Promo</option>
              </select>

              {/* Active Filter */}
              <select
                className="form-select"
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="" disabled>
                  Filtrer par statut
                </option>
                <option value="">Tous</option>
                <option value="true">Actif</option>
                <option value="false">Inactif</option>
              </select>

              {/* Reusable Filter */}
              <select
                className="form-select"
                value={isReusable}
                onChange={(e) => setIsReusable(e.target.value)}
                style={{ width: "130%" }}
              >
                <option value="" disabled>
                  Filtrer par réutilisable
                </option>
                <option value="">Tous</option>
                <option value="true">Réutilisable</option>
                <option value="false">One-time</option>
              </select>

              {/* Search */}
              <div className="search-form">
                <div className="input-field">
                  <MagnifyingGlassIcon width={20} />
                  <input
                    type="text"
                    placeholder="Search"
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive mt-3">
            <RenderTable
              loading={isLoading}
              render={() =>
                rewards.length > 0 ? (
                  <RewardList
                    rewards={rewards}
                    onSelect={handleRewardSelection}
                  />
                ) : (
                  <p className="text-center">{general.fr.noData}</p>
                )
              }
            />
          </div>
        </BoxWrapper>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          siblingCount={1}
          onPageChange={setCurrentPage}
          totalCount={data?.meta?.totalPages || 1}
          pageSize={10}
        />

        {showConfigModal && (
          <>
            <div className="modal fade show d-block">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content p-3">
                  <h5>Configurer les points</h5>

                  <select
                    className="form-select mt-3"
                    value={configType}
                    onChange={(e) => setConfigType(e.target.value)}
                  >
                    <option value="">Sélectionner un type</option>
                    <option value="RIDE">Courses</option>

                    {/* <option value="OFFER">Offre</option>
                    <option value="PACK">Pack</option>
                    <option value="EVENT">Événement</option>
                    <option value="DISCOUNT">Promo</option>
                    <option value="PLACE">Place</option>
                    <option value="FEATURE_UNLOCK">Feature Unlock</option> */}
                  </select>

                  <input
                    type="number"
                    className="form-control mt-2"
                    value={points}
                    onChange={(e) => setPoints(e.target.value)}
                    placeholder="Points de base"
                  />

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowConfigModal(false)}
                    >
                      Annuler
                    </button>

                    <button
                      className="btn btn-success"
                      onClick={() => {
                        if (points === "") return;

                        updatePointsMutation.mutate({
                          points: Number(points),
                          type: configType,
                        });
                      }}
                    >
                      {updatePointsMutation.isLoading ? (
                        <CSpinner size="sm" />
                      ) : (
                        "Sauvegarder"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-backdrop fade show"></div>
          </>
        )}
      </section>
    </AdminRoute>
  );
}
